import React from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import { Col, Spinner } from "reactstrap";
import classNames from "classnames";
import FormGenerator from "components/customs/form-generator/input";
import CustomSelect from "components/customs/dropdown";
import { useNavigate } from "react-router";
import { addProjectDropdownStyles } from "constants/style";
import {
  FormActionsSection,
  FormLabel,
  FormRow,
  RequiredIndicator,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import { mobileValidationSchema } from "validation";
import { POSITION_REGIONAL_DIRECTOR } from "../constants";

const validSchema = mobileValidationSchema.shape({
  lastName: Yup.string().required("Required"),
  firstName: Yup.string().required("Required"),
  region: Yup.object().required("Required"),
});

const LastNameFiledContainer = styled(FormRow)`
  margin-top: 0;
`;

const DirectorForm = ({
  handleOnSubmit,
  formFields,
  setFormFields,
  regionOptions,
  agencyOptions,
  selectedAgency,
  setAgencyId,
  setSelectedAgency,
  agencyError,
  isLoading,
  agencyId,
}) => {
  const navigate = useNavigate();

  return (
    <Formik
      validationSchema={validSchema}
      initialValues={formFields}
      enableReinitialize
      onSubmit={() => handleOnSubmit()}
    >
      {({ handleChange, values }) => (
        <Form>
          <Field name="lastName">
            {({ form: { touched, errors } }) => (
              <LastNameFiledContainer>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Last Name"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide your Last Name"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="text"
                    name="lastName"
                    className={classNames({
                      "is-invalid": errors?.lastName && touched?.lastName,
                    })}
                    value={values.lastName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        lastName: evt.target.value || "",
                      });
                    }}
                  />

                  <ErrorMessage name="lastName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </LastNameFiledContainer>
            )}
          </Field>

          <Field name="firstName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"First Name"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide your First Name"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="text"
                    name="firstName"
                    className={classNames({
                      "is-invalid": errors?.firstName && touched?.firstName,
                    })}
                    value={values.firstName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        firstName: evt.target.value || "",
                      });
                    }}
                  />

                  <ErrorMessage name="firstName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="mobile">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Contact Number"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide your Contact Number"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="mobile"
                    name="mobile"
                    className={classNames({
                      "is-invalid": errors?.mobile && touched?.mobile,
                    })}
                    value={values.mobile || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        mobile: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="mobile">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="region">
            {({ form: { touched, errors, setFieldValue } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>
                    {"Region"}
                    {RequiredIndicator}
                  </FormLabel>
                  <p>{"Provide your Region"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="region"
                    styles={addProjectDropdownStyles}
                    options={regionOptions}
                    value={values.region}
                    className={classNames({
                      "is-invalid": errors?.region && touched?.region,
                    })}
                    onChange={(option) => {
                      setFieldValue("region", option);

                      setFormFields({
                        ...formFields,
                        region: option,
                      });
                    }}
                  />
                  <ErrorMessage name="region">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormRow>
            <Col
              xs="12"
              md="6"
            >
              <FormLabel>
                {"Agency"}
                {RequiredIndicator}
              </FormLabel>
              <p>{"Provide your Agency"}</p>
            </Col>
            <Col
              xs="12"
              md="6"
            >
              <CustomSelect
                placeholder=""
                name="agency"
                styles={addProjectDropdownStyles}
                options={agencyOptions}
                value={selectedAgency}
                onChange={(option) => {
                  setAgencyId(option.value);
                  setSelectedAgency(option);
                }}
                filterOption={(option, searchValue) =>
                  option?.label
                    ?.toLowerCase()
                    ?.includes(searchValue?.toLowerCase()) ||
                  option?.data?.acronym
                    ?.toLowerCase()
                    ?.includes(searchValue?.toLowerCase())
                }
              />
              <small className="field-error text-danger">{agencyError}</small>
            </Col>
          </FormRow>

          <Field name="cosLastName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>{"Chief of Staff Last Name"}</FormLabel>
                  <p>{"Provide the Chief of Staff's Last Name"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosLastName"
                    className={classNames({
                      "is-invalid": errors?.cosLastName && touched?.cosLastName,
                    })}
                    value={values.cosLastName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosLastName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosLastName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosFirstName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>{"Chief of Staff First Name"}</FormLabel>
                  <p>{"Provide the Chief of Staff's First Name"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosFirstName"
                    className={classNames({
                      "is-invalid":
                        errors?.cosFirstName && touched?.cosFirstName,
                    })}
                    value={values.cosFirstName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosFirstName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosFirstName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosNumber">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormLabel>{"Contact Number"}</FormLabel>
                  <p>{"Provide the Chief of Staff's Contact Number"}</p>
                </Col>
                <Col
                  xs="12"
                  md="6"
                >
                  <FormGenerator
                    type="mobile"
                    name="cosNumber"
                    className={classNames({
                      "is-invalid": errors?.cosNumber && touched?.cosNumber,
                    })}
                    value={values.cosNumber || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        cosNumber: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosNumber">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormActionsSection>
            <StyledCancelButton
              outline
              disabled={isLoading}
              onClick={() => {
                navigate("/directory", {
                  state: {
                    view: POSITION_REGIONAL_DIRECTOR,
                    agencySelect: agencyId,
                  },
                });
              }}
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledSaveButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledSaveButton>
          </FormActionsSection>
        </Form>
      )}
    </Formik>
  );
};

export default DirectorForm;
